import { useMemo } from 'react';
import { useGlobalQuery } from 'state/query-client/hooks';
import {
  ActiveIntegrationConnection,
  getIntegrationConnections,
  IntegrationConnectionsListParams,
} from 'services/integration-service';
import { useConfig } from './use-config';

export const useGetIntegrationConnections = (
  params?: IntegrationConnectionsListParams,
  enabled = true
) => {
  const { configurationSettings } = useConfig();

  return useGlobalQuery<ActiveIntegrationConnection[]>({
    queryKey: ['integrationConnections', params?.channel_id, params?.agent_id, params?.status],
    queryFn: async () => {
      if (!configurationSettings) {
        throw new Error('Configuration settings are not available');
      }

      return getIntegrationConnections(configurationSettings, params);
    },
    enabled: !!configurationSettings && enabled,
  });
};

export const useGetIntegrationConnectionsByChannels = (
  channelIds: string[],
  agentId: string,
  enabled = true
) => {
  const { configurationSettings } = useConfig();
  const channelKey = channelIds.filter(Boolean).join('|');
  const uniqueChannelIds = useMemo(
    () => [...new Set(channelIds.filter(Boolean))],
    // channelKey is a stable serialization of channelIds
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [channelKey]
  );

  return useGlobalQuery<Record<string, ActiveIntegrationConnection[]>>({
    queryKey: ['integrationConnections', agentId, uniqueChannelIds],
    queryFn: async () => {
      if (!configurationSettings) {
        throw new Error('Configuration settings are not available');
      }

      const entries = await Promise.all(
        uniqueChannelIds.map(async (channel_id) => {
          const connections = await getIntegrationConnections(configurationSettings, {
            channel_id,
            agent_id: agentId,
          });
          return [channel_id, connections] as const;
        })
      );

      return Object.fromEntries(entries);
    },
    enabled: !!configurationSettings && enabled && !!agentId && uniqueChannelIds.length > 0,
  });
};
